import React from 'react';
import PieChart from './PieChart';

const DashboardView = ({ people, capabilities, coverageCalculator }) => {
  if (!people.length || !capabilities.length) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Management Dashboard</h2>
        <div className="text-center py-8">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <p className="text-gray-500 mb-4">No data available yet</p>
          <p className="text-sm text-gray-400">Add people and capabilities in the Editor to see your team overview</p>
        </div>
      </div>
    );
  }

  // Calculate overall metrics
  const totalCapabilities = capabilities.length;
  const totalPeople = people.length;
  const totalSkills = coverageCalculator.getAllSkills(people, capabilities).length;
  
  // Calculate country distribution
  const countryDistribution = people.reduce((acc, person) => {
    const country = person.country || 'Unknown';
    if (!acc[country]) {
      acc[country] = { count: 0, people: [] };
    }
    acc[country].count++;
    acc[country].people.push(person);
    return acc;
  }, {});
  
  const totalCountries = Object.keys(countryDistribution).filter(c => c !== 'Unknown').length;
  const countriesData = Object.entries(countryDistribution)
    .map(([country, data]) => ({ country, ...data }))
    .sort((a, b) => b.count - a.count);
  
  // Calculate capability coverage overview
  const capabilityOverview = capabilities.map(capability => {
    const teamCoverage = coverageCalculator.getTeamCoverage(people, capability);
    const contributors = people.filter(person => 
      coverageCalculator.calculateCoverage(person, capability) > 0
    );
    
    const skillGaps = (capability.required_skills || []).filter(skill => {
      const peopleWithSkill = people.filter(p => p.skills && p.skills.includes(skill)).length;
      return peopleWithSkill === 0;
    });

    return {
      capability,
      teamCoverage,
      contributors,
      skillGaps: skillGaps.length,
      status: teamCoverage >= 80 ? 'excellent' : 
              teamCoverage >= 60 ? 'good' : 
              teamCoverage >= 40 ? 'needs-improvement' : 'critical'
    };
  });

  // Overall team health metrics
  const overallCoverage = Math.round(
    capabilityOverview.reduce((sum, cap) => sum + cap.teamCoverage, 0) / totalCapabilities
  );
  
  const criticalCapabilities = capabilityOverview.filter(cap => cap.status === 'critical').length;
  const excellentCapabilities = capabilityOverview.filter(cap => cap.status === 'excellent').length;

  // People skills completeness
  const peopleAnalysis = people.map(person => {
    const personSkills = person.skills || [];
    const allRequiredSkills = new Set();
    capabilities.forEach(cap => {
      (cap.required_skills || []).forEach(skill => allRequiredSkills.add(skill));
    });
    
    const skillsCompleteness = Math.round(
      (personSkills.filter(skill => allRequiredSkills.has(skill)).length / allRequiredSkills.size) * 100
    );
    
    const capabilitiesContribution = capabilities.filter(capability =>
      coverageCalculator.calculateCoverage(person, capability) > 0
    ).length;

    return {
      person,
      skillsCompleteness,
      capabilitiesContribution,
      totalSkills: personSkills.length
    };
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'bg-green-100 text-green-800 border-green-200';
      case 'good': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'needs-improvement': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'excellent': return '🟢';
      case 'good': return '🔵';
      case 'needs-improvement': return '🟡';
      case 'critical': return '🔴';
      default: return '⚪';
    }
  };

  return (
    <div className="space-y-6 w-full">
      {/* Header Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4 w-full">
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <div className="text-3xl font-bold text-blue-600">{overallCoverage}%</div>
          <div className="text-sm text-gray-600 mt-1">Overall Coverage</div>
          <div className="mt-2">
            <PieChart percentage={overallCoverage} size={32} strokeWidth={3} />
          </div>
        </div>
        
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <div className="text-3xl font-bold text-green-600">{totalPeople}</div>
          <div className="text-sm text-gray-600 mt-1">Team Members</div>
          <div className="text-xs text-gray-500 mt-2">{totalCountries} countries • {totalSkills} skills</div>
        </div>
        
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <div className="text-3xl font-bold text-purple-600">{totalCapabilities}</div>
          <div className="text-sm text-gray-600 mt-1">Capabilities</div>
          <div className="text-xs text-gray-500 mt-2">{excellentCapabilities} well covered</div>
        </div>
        
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <div className="text-3xl font-bold text-red-600">{criticalCapabilities}</div>
          <div className="text-sm text-gray-600 mt-1">Critical Gaps</div>
          <div className="text-xs text-gray-500 mt-2">Need immediate attention</div>
        </div>
      </div>

      {/* Country Distribution */}
      {totalCountries > 1 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Global Team Distribution</h3>
            <p className="text-sm text-gray-500 mt-1">Team members across {totalCountries} countries</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-4">
              {countriesData.map((countryData, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-2 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-blue-600">{countryData.country}</span>
                  </div>
                  <div className="text-lg font-semibold text-gray-900">{countryData.count}</div>
                  <div className="text-xs text-gray-500">
                    {countryData.count === 1 ? 'person' : 'people'}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {Math.round((countryData.count / totalPeople) * 100)}%
                  </div>
                  
                  {/* People names for this country */}
                  <div className="mt-2">
                    <div className="flex flex-wrap justify-center gap-1">
                      {countryData.people.slice(0, 3).map((person, idx) => (
                        <div
                          key={idx}
                          className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-medium"
                          title={person.name}
                        >
                          {person.name.charAt(0).toUpperCase()}
                        </div>
                      ))}
                      {countryData.people.length > 3 && (
                        <div className="w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs">
                          +{countryData.people.length - 3}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-2 gap-6 w-full">
        {/* Capability Overview */}
        <div className="bg-white shadow rounded-lg flex-1 min-h-0">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Capability Health Overview</h3>
            <p className="text-sm text-gray-500 mt-1">Team coverage across all capabilities</p>
          </div>
          <div className="p-6">
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {capabilityOverview.map((overview, index) => (
                <div key={index} className={`border rounded-lg p-4 ${getStatusColor(overview.status)}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span>{getStatusIcon(overview.status)}</span>
                      <h4 className="font-medium">{overview.capability.name}</h4>
                    </div>
                    <div className="flex items-center space-x-2">
                      <PieChart percentage={overview.teamCoverage} size={24} strokeWidth={2} />
                      <span className="text-sm font-medium">{overview.teamCoverage}%</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-4">
                      <span>👥 {overview.contributors.length} contributors</span>
                      {overview.skillGaps > 0 && (
                        <span className="text-red-600">⚠️ {overview.skillGaps} skill gaps</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Contributor avatars */}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex -space-x-1">
                      {overview.contributors.slice(0, 5).map((contributor, idx) => (
                        <div
                          key={idx}
                          className="w-6 h-6 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center text-xs font-medium text-gray-700"
                          title={contributor.name}
                        >
                          {contributor.name.charAt(0).toUpperCase()}
                        </div>
                      ))}
                      {overview.contributors.length > 5 && (
                        <div className="w-6 h-6 rounded-full bg-gray-100 border-2 border-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                          +{overview.contributors.length - 5}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Team Members Overview */}
        <div className="bg-white shadow rounded-lg flex-1 min-h-0">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Team Members Overview</h3>
            <p className="text-sm text-gray-500 mt-1">Individual skill completeness and contributions</p>
          </div>
          <div className="p-6">
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {peopleAnalysis
                .sort((a, b) => b.skillsCompleteness - a.skillsCompleteness)
                .map((analysis, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                        {analysis.person.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{analysis.person.name}</h4>
                        <div className="text-xs text-gray-600">
                          {analysis.person.country && (
                            <span className="inline-flex items-center mr-2">
                              <span className="w-4 h-4 bg-blue-100 rounded text-xs font-bold text-blue-600 flex items-center justify-center mr-1">
                                {analysis.person.country}
                              </span>
                            </span>
                          )}
                          {analysis.totalSkills} skills • {analysis.capabilitiesContribution} capabilities
                        </div>
                      </div>
                    </div>
                    <PieChart percentage={analysis.skillsCompleteness} size={32} strokeWidth={3} />
                  </div>
                  
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Skills Completeness</span>
                      <span>{analysis.skillsCompleteness}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          analysis.skillsCompleteness >= 80 ? 'bg-green-500' :
                          analysis.skillsCompleteness >= 60 ? 'bg-blue-500' :
                          analysis.skillsCompleteness >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${analysis.skillsCompleteness}%` }}
                      />
                    </div>
                  </div>

                  {/* Certifications */}
                  {analysis.person.certifications && analysis.person.certifications.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {analysis.person.certifications.slice(0, 3).map((cert, idx) => (
                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {cert}
                        </span>
                      ))}
                      {analysis.person.certifications.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                          +{analysis.person.certifications.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Action Items */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recommended Actions</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4 w-full">
            {criticalCapabilities > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-red-600 mr-2">🚨</span>
                  <h4 className="font-medium text-red-900">Critical Capabilities</h4>
                </div>
                <p className="text-sm text-red-800">
                  {criticalCapabilities} capabilities need immediate attention. Consider hiring or intensive training.
                </p>
              </div>
            )}
            
            {peopleAnalysis.filter(p => p.skillsCompleteness < 50).length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-yellow-600 mr-2">📚</span>
                  <h4 className="font-medium text-yellow-900">Training Opportunities</h4>
                </div>
                <p className="text-sm text-yellow-800">
                  {peopleAnalysis.filter(p => p.skillsCompleteness < 50).length} team members could benefit from additional skill development.
                </p>
              </div>
            )}
            
            {excellentCapabilities > totalCapabilities * 0.7 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-green-600 mr-2">🎯</span>
                  <h4 className="font-medium text-green-900">Strong Foundation</h4>
                </div>
                <p className="text-sm text-green-800">
                  Team has strong coverage. Consider advanced certifications or new capability development.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardView;